import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { logger } from "../../observability/LoggerFactory";
import { useState, useRef, useCallback, useEffect } from "react";
import type { paths } from "../../api/generated/api-types";

type ListResponse =
  paths["/sites/verifications"]["get"]["responses"]["200"]["content"]["application/json"];

export type SiteVerificationItem = ListResponse["items"][number];

type ScreenState =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "data"; items: SiteVerificationItem[]; totalCount: number }
  | { kind: "error"; message: string };

const DEFAULT_PAGE_SIZE = 25;

export interface UseSiteVerificationsResult {
  state: ScreenState;
  fetching: boolean;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  refetch: () => void;
}

export function useSiteVerifications(): UseSiteVerificationsResult {
  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [fetching, setFetching] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async (currentPage: number, currentPageSize: number) => {
    if (abortRef.current !== null) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setFetching(true);

    try {
      const parameters = new URLSearchParams({
        page: String(currentPage),
        pageSize: String(currentPageSize),
      });
      const response = await fetchWithRefresh(`/api/sites/verifications?${parameters}`, { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = (await response.json()) as ListResponse;
      if (data.items.length === 0 && data.totalCount === 0) {
        setState({ kind: "empty" });
      } else {
        setState({ kind: "data", items: data.items, totalCount: data.totalCount });
      }
    } catch (error: unknown) {
      if (controller.signal.aborted) {
        return;
      }
      const message = error instanceof Error ? error.message : "Unknown error";
      logger.error({ err: message, component: "useSiteVerifications" }, "Failed to load site verifications");
      setState({ kind: "error", message });
    } finally {
      if (!controller.signal.aborted) {
        setFetching(false);
      }
    }
  }, []);

  useEffect(() => {
    void load(page, pageSize);
    return () => {
      if (abortRef.current !== null) {
        abortRef.current.abort();
      }
    };
  }, [page, pageSize, load]);

  const refetch = useCallback(() => {
    void load(page, pageSize);
  }, [page, pageSize, load]);

  return {
    state,
    fetching,
    page,
    pageSize,
    setPage,
    setPageSize,
    refetch,
  };
}
